import { Injectable, inject, OnDestroy, signal, computed, effect } from '@angular/core';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import {
  Observable,
  of as observableOf,
  switchMap,
  map,
  tap,
  catchError,
  Subject,
  takeUntil,
  forkJoin,
  retryWhen,
  delay,
  take
} from 'rxjs';


// salesonepro
import { ApiService, AuthService, StorageService, ToasterService } from '@salesonepro/services';
import { ModalPopupService } from '@salesonepro/components';

// components
import { ImportedUserSetupComponent } from './imported-user-setup/imported-user-setup.component';
import { CustomerPopupComponent } from '../customer-popup/customer-popup.component';
import { User } from '@salesonepro/models';

// Cache duration constants
const CACHE_DURATION = {
  COUNTRY_LIST: 24 * 60 * 60 * 1000, // 24 hours
  ORDER_OPTIONS: 60 * 60 * 1000,     // 1 hour
  USER_INFO: 15 * 60 * 1000          // 15 minutes
};

const RETRY_CONFIG = {
  RETRY_ATTEMPTS: 2,
  RETRY_DELAY: 2000
};

export interface UserInfo {
  id: number;
  name: string;
  email: string;
  imported_user: boolean;
  first_name?: string;
  last_name?: string;
  avatar?: string;
  settings?: Record<string, any>;
}

interface CountryStateItem {
  id: number;
  name: string;
  code: string;
  states?: StateItem[];
}

interface StateItem {
  id: number;
  name: string;
  code: string;
}

interface OrderOption {
  id: number;
  name: string;
  value: string;
  type: string;
  category?: string;
}

interface CustomerInfo {
  id: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

interface LoginCredentials {
  username: string;
  password: string;
  grant_type: string;
}

export interface LoginFlowResult {
  success: boolean;
  requiresSetup?: boolean;
  requiresCustomerSelection?: boolean;
  user?: UserInfo;
  customers?: any[];
  error?: any;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private apiService = inject(ApiService);
  private storage = inject(StorageService);
  private toastr = inject(ToasterService);
  private authService = inject(AuthService);
  private modal = inject(ModalPopupService);
  private router = inject(Router);

  // Angular Signals for reactive state management
  private userSignal = signal<UserInfo | null>(null);
  private isLoadingUserSignal = signal<boolean>(false);

  // Public computed signals
  public currentUser = computed(() => this.userSignal());
  public isUserLoading = computed(() => this.isLoadingUserSignal());
  public isLoggedIn = computed(() => !!this.userSignal());
  public userDisplayName = computed(() => {
    const user = this.userSignal();
    if (!user) return '';
    return user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.name;
  });
  // public isImportedUser = computed(() => {
  //   const user = this.userSignal();
  //   return user?.imported_user === true;
  // });

  constructor() {
    // Initialize user from storage on service creation
    this.initializeUserFromStorage();

    // Effect to sync user changes to storage
    effect(() => {
      const user = this.userSignal();
      if (user) {
        this.storage.store('user', user);
      }
    });
  }

  /**
   * Initialize user signal from storage on service startup
   * This ensures user state persists across page refreshes
   */
  private initializeUserFromStorage(): void {
    const storedUser = this.storage.retrieve('user');
    if (storedUser) {
      // Ensure user has settings property
      if (!storedUser.settings) {
        storedUser.settings = {};
      }
      this.userSignal.set(storedUser);
    }
  }

  /**
   * Clear user state (useful for logout)
   */
  public clearUserState(): void {
    this.userSignal.set(null);
    this.storage.clear('user');
  }

  /**
   * Enhanced login flow that handles imported users and customer selection
   * @param credentials - User login credentials containing username and password
   * @returns Observable that emits the login flow result with success status and next steps
   */
  performLogin(credentials: LoginCredentials): Observable<LoginFlowResult> {
    // Input validation
    if (!credentials || !credentials.username || !credentials.password) {
      return observableOf({
        success: false,
        error: 'Invalid credentials: username and password are required'
      });
    }

    if (credentials.username.trim().length === 0 || credentials.password.trim().length === 0) {
      return observableOf({
        success: false,
        error: 'Invalid credentials: username and password cannot be empty'
      });
    }

    // Clear cached user data before new login attempt
    this.storage.clear('user');
    return this.authService.attemptAuth('', credentials).pipe(
      switchMap(() => this.handlePostLoginFlow()),
      catchError(error => observableOf({ success: false, error }))
    );
  }

  /**
   * Handle the complete post-login flow
   */
  private handlePostLoginFlow(): Observable<LoginFlowResult> {
    return this.getCurrentUserInfo().pipe(
      switchMap((user: UserInfo) => {
        if (user.imported_user) {
          return this.handleImportedUser(user);
        } else {
          return this.handleRegularUser(user);
        }
      })
    );
  }

  /**
   * Get current user information
   */
  private getCurrentUserInfo(): Observable<UserInfo> {
    this.isLoadingUserSignal.set(true);
    return this.apiService.get('/auth-user').pipe(
      tap(user => {
        if (user) {
          this.setUserData(user);
        }
        this.isLoadingUserSignal.set(false);
      }),
      catchError(error => {
        this.isLoadingUserSignal.set(false);
        throw error;
      }),
      map(data => data)
    );
  }

  updateUserInfo() {
    this.isLoadingUserSignal.set(true);
    this.apiService.get('/auth-user')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.setUserData(response);
          this.isLoadingUserSignal.set(false);
        },
        error: (error) => {
          this.isLoadingUserSignal.set(false);
        }
      });
  }

  /**
   * Set user data using signals (replaces old setUserSubject)
   */
  private setUserData(user: UserInfo): void {
    if (!user.settings) {
      user.settings = {};
    }
    this.userSignal.set(user);
  }

  /**
   * Handle imported user flow - show setup popup
   */
  private handleImportedUser(user: UserInfo): Observable<LoginFlowResult> {
    return new Observable(observer => {
      // Immediately notify that setup is required (this will hide the spinner)
      observer.next({
        success: true,
        requiresSetup: true,
        user
      });

      const modalRef = this.modal.open(ImportedUserSetupComponent, {
        title: 'Complete Account Setup',
        hideBackdrop: false,
        size: 'lg',
        position: 'center',
        disableBackdropClick: true,
        hideCloseButton: true,
        headerClass: 'bg-primary text-white'
      });

      modalRef.afterClosed$.pipe(
        takeUntil(this.destroy$)
      ).subscribe(result => {
        if (result?.data) {
          // Update user info after successful setup
          this.updateUserInfo();
          // Continue with customer selection flow
          this.handleCustomerSelection().pipe(
            takeUntil(this.destroy$)
          ).subscribe(customerResult => {
            observer.next(customerResult);
            observer.complete();
          });
        } else {
          // User cancelled setup, logout
          this.authService.purgeAuth();
          observer.next({
            success: false,
            requiresSetup: true,
            error: 'Account setup was cancelled'
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Handle regular user flow - proceed to customer selection
   */
  private handleRegularUser(user: UserInfo): Observable<LoginFlowResult> {
    return this.handleCustomerSelection();
  }

  /**
   * Handle customer selection flow
   */
  private handleCustomerSelection(): Observable<LoginFlowResult> {
    // Check if default customer is already set
    if (this.storage.retrieve('default_customer')) {
      this.redirectToDashboard();
      return observableOf({ success: true });
    }

    // Fetch customer information
    const params = new HttpParams();
    return this.getCustomerInfo(params).pipe(
      switchMap((customers: any[]) => {
        if (customers.length === 0) {
          // No customers found, logout
          this.authService.purgeAuth();
          return observableOf({
            success: false,
            error: 'No customer accounts found for this user'
          });
        } else if (customers.length === 1) {
          // Single customer, set as default and redirect
          this.setDefaultCustomer(customers[0]);
          this.redirectToDashboard();
          return observableOf({ success: true, customers });
        } else {
          // Multiple customers, show selection popup
          return this.showCustomerSelectionPopup(customers);
        }
      }),
      catchError(error => observableOf({
        success: false,
        error: 'Failed to fetch customer information'
      }))
    );
  }

  /**
   * Show customer selection popup
   */
  private showCustomerSelectionPopup(customers: any[]): Observable<LoginFlowResult> {
    return new Observable(observer => {
      observer.next({
        success: true
      });
      this.storage.store('customers', customers);

      const modalRef = this.modal.open(CustomerPopupComponent, {
        title: 'SELECT CUSTOMER ACCOUNT TO MANAGE',
        hideBackdrop: false,
        size: 'lg',
        position: 'top',
        disableBackdropClick: true,
        headerClass: 'bg-primary text-white',
        input: { customer: customers, refresh: false },
        hideCloseButton: true
      });

      modalRef.afterClosed$.pipe(
        takeUntil(this.destroy$)
      ).subscribe(result => {
        if (result?.data) {
          this.setDefaultCustomer(result.data);
          this.redirectToDashboard();
          observer.next({
            success: true,
            customers,
            requiresCustomerSelection: true
          });
        } else {
          // User cancelled selection, logout
          this.authService.purgeAuth();
          observer.next({
            success: false,
            requiresCustomerSelection: true,
            error: 'Customer selection was cancelled'
          });
        }
        observer.complete();
      });
    });
  }

  /**
   * Set default customer and initialize related data
   */
  private setDefaultCustomer(customer: CustomerInfo): void {
    this.storage.store('default_customer', customer);
    this.storage.store('selected_customer', customer);

    // Load customer-specific data after customer selection
    this.loadCustomerData();
  }

  /**
   * Redirect to dashboard
   */
  private redirectToDashboard(): void {
    // setTimeout(() => {
    //   this.accountService.loadCartSummary();
    // }, 100);

    this.router.navigateByUrl('/account/dashboard');
  }

  /**
   * Check if current user is imported user
   */
  isImportedUser(): boolean {
    const user = this.storage.retrieve('user');
    return user?.imported_user === true;
  }

  /**
   * Force user setup flow (useful for testing or manual trigger)
   */
  triggerUserSetup(): Observable<any> {
    const user = this.storage.retrieve('user');
    if (!user) {
      return observableOf({ success: false, error: 'No user found' });
    }

    return this.handleImportedUser(user);
  }

  /**
 * Batch customer-specific data loading after customer selection
 */
  loadCustomerData() {
    const countryListRequest = this.shouldFetchCountryList() ?
      this.apiService.get('/country_state_list') : observableOf(this.storage.retrieve('country-state-list'));

    const orderOptionsRequest = this.shouldFetchOrderOptions() ?
      this.apiService.get('/order_option_list') : observableOf(this.storage.retrieve('orderOptions'));

    forkJoin({
      countries: countryListRequest,
      orderOptions: orderOptionsRequest
    }).pipe(
      takeUntil(this.destroy$),
      retryWhen(errors =>
        errors.pipe(
          delay(RETRY_CONFIG.RETRY_DELAY),
          take(RETRY_CONFIG.RETRY_ATTEMPTS)
        )
      )
    ).subscribe({
      next: (results) => {
        // Handle country list
        if (results.countries && this.shouldFetchCountryList()) {
          this.storage.store('country-state-list', results.countries);
          this.storage.store('country-state-list-timestamp', Date.now().toString());
        }

        // Handle order options
        if (results.orderOptions && this.shouldFetchOrderOptions()) {
          this.storage.store('orderOptions', results.orderOptions);
          this.storage.store('orderOptions-timestamp', Date.now().toString());
        }
      },
      error: (error) => {
        // Fallback to individual calls if batch fails
        this.getCountryStateList();
        this.getOrderOptions();
      }
    });
  }


  /**
   * Get comprehensive login flow status information
   * @returns Object containing authentication status, customer selection status, and setup requirements
   */
  getLoginStatus(): {
    isAuthenticated: boolean;
    hasDefaultCustomer: boolean;
    isImportedUser: boolean;
    needsSetup: boolean;
  } {
    const isAuthenticated = !!this.authService.getToken() && !this.authService.isTokenExpired();
    const hasDefaultCustomer = !!this.storage.retrieve('default_customer');
    const user = this.storage.retrieve('user');
    const isImportedUser = user?.imported_user === true;

    return {
      isAuthenticated,
      hasDefaultCustomer,
      isImportedUser,
      needsSetup: isAuthenticated && isImportedUser && !hasDefaultCustomer
    };
  }

  /**
   * Clear all auth data and redirect to login
   */
  logout(): void {
    this.authService.purgeAuth();
    this.clearUserState(); // Use new signal-based clearing
    this.clearCache();
    this.router.navigateByUrl('/login');
  }

  clearCache() {
    this.storage.clear('country-state-list');
    this.storage.clear('country-state-list-timestamp');
    this.storage.clear('orderOptions');
    this.storage.clear('orderOptions-timestamp');
  }

  getCustomerInfo(params: HttpParams): Observable<CustomerInfo[]> {
    return this.apiService.get('/contact/customers', params).pipe(
      map(
        data => { return data; }
      ));
  }

  // forgot password
  forgotPassword(data: { email: string }): Observable<any> {
    return this.apiService
      .post('/account/forgot_password', data).pipe(
        tap(
          _ => {
            this.toastr.success(
              ' Password reset link has been sent to your email '
            );
          },
          _ =>
            this.toastr.success(
              'Your Email is not registered with us'
            )
        )
      );
  }

  getCountryStateList() {
    if (this.shouldFetchCountryList()) {
      this.apiService.get('/country_state_list')
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          this.storage.store('country-state-list', response);
          this.storage.store('country-state-list-timestamp', Date.now().toString());
        });
    }
  }

  getOrderOptions() {
    if (this.shouldFetchOrderOptions()) {
      this.apiService.get('/order_option_list')
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          this.storage.store('orderOptions', response);
          this.storage.store('orderOptions-timestamp', Date.now().toString());
        });
    }
  }

  private shouldFetchCountryList(): boolean {
    const cached = this.storage.retrieve('country-state-list');
    if (!cached) return true;

    // Check if cache is older than configured duration
    const cacheTime = this.storage.retrieve('country-state-list-timestamp');
    if (!cacheTime) return true;

    return (Date.now() - parseInt(cacheTime)) > CACHE_DURATION.COUNTRY_LIST;
  }

  private shouldFetchOrderOptions(): boolean {
    const cached = this.storage.retrieve('orderOptions');
    if (!cached) return true;

    // Check if cache is older than configured duration
    const cacheTime = this.storage.retrieve('orderOptions-timestamp');
    if (!cacheTime) return true;

    return (Date.now() - parseInt(cacheTime)) > CACHE_DURATION.ORDER_OPTIONS;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
